import type {
    ExecutionCascadeEvent,
    ExecutionCompletedEvent,
    ExecutionFailedEvent,
    ExecutionIterationCompletedEvent,
    ExecutionIterationStartedEvent,
    ExecutionJobChangedEvent,
    ExecutionProgressEvent,
    ExecutionStartedEvent,
    SidecarRestartedEvent,
    SidecarSpawnedEvent,
    SidecarStoppedEvent,
    TraceCompletedEvent,
    TraceProgressNotification,
    TraceProgressEvent,
    TraceSource,
    TraceStartedEvent,
    TraceSpanKind,
    TraceSpanStatus,
} from "@magic-md/engine-client/protocol";
import { create } from "zustand";
import { getNotificationBus } from "./notification-bus";
import { tabSessionRegistry } from "./tab-session-registry";

const MAX_TRACE_RUNS = 30;
const MAX_EVENTS_PER_RUN = 4_000;

export interface TraceSpan {
    id: string;
    traceId: string;
    parentId?: string;
    kind: TraceSpanKind;
    name: string;
    status: TraceSpanStatus;
    startedAt: string;
    completedAt?: string;
    durationMs?: number;
    unitId?: string;
    unitType?: string;
    pluginName?: string;
    actionName?: string;
    sidecarId?: string;
    method?: string;
    source?: TraceSource;
    extensionId?: string;
    attrs?: Record<string, unknown>;
    error?: string;
}

export interface TraceEventRecord {
    id: string;
    traceId: string;
    spanId?: string;
    timestamp: string;
    type: string;
    unitId?: string;
    message?: string;
    attrs?: Record<string, unknown>;
}

export interface TraceRun {
    traceId: string;
    executionId: string;
    sessionId: string;
    filePath?: string;
    target: "unit" | "all";
    unitId?: string;
    unitIds?: string[];
    action?: string;
    source?: TraceSource;
    extensionId?: string;
    method?: string;
    status: TraceSpanStatus;
    startedAt: string;
    completedAt?: string;
    durationMs?: number;
    baseRevision?: number;
    currentRevision?: number;
    eventCount: number;
    droppedCount: number;
    spans: TraceSpan[];
    events: TraceEventRecord[];
}

interface TraceState {
    runs: TraceRun[];
    selectedTraceId: string | null;
    recording: boolean;
    setRecording: (recording: boolean) => void;
    selectTrace: (traceId: string | null) => void;
    importTrace: (run: TraceRun) => void;
    deleteTrace: (traceId: string) => void;
    clear: () => void;
    exportTrace: (traceId: string) => string | null;
    ingestNotification: (method: string, params: unknown) => void;
}

let eventSeq = 0;

function nowIso(): string {
    return new Date().toISOString();
}

function eventTimestamp(params: unknown): string {
    const value = (params as { timestamp?: unknown } | null)?.timestamp;
    return typeof value === "string" && value.length > 0 ? value : nowIso();
}

function statusFromJob(status: ExecutionJobChangedEvent["status"]): TraceSpanStatus {
    if (status === "running") return "running";
    if (status === "failed") return "failed";
    if (status === "cancelled") return "cancelled";
    return "completed";
}

function durationBetween(startedAt: string, completedAt?: string): number | undefined {
    if (!completedAt) return undefined;
    const start = new Date(startedAt).getTime();
    const end = new Date(completedAt).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end)) return undefined;
    return Math.max(0, end - start);
}

function getTraceId(params: unknown): string | null {
    const value = (params as { traceId?: unknown } | null)?.traceId;
    return typeof value === "string" && value.length > 0 ? value : null;
}

function stringField(params: unknown, key: string): string | undefined {
    const value = (params as Record<string, unknown> | null)?.[key];
    return typeof value === "string" && value.length > 0 ? value : undefined;
}

function resolveEventFilePath(params: unknown): string | undefined {
    const filePath = stringField(params, "filePath");
    if (filePath) return filePath;
    const sessionId = stringField(params, "sessionId");
    if (!sessionId) return undefined;
    const tabId = tabSessionRegistry.getTabForSession(sessionId);
    return tabId ? tabSessionRegistry.getFilePathForTab(tabId) : undefined;
}

function jobSpanId(executionId: string): string {
    return `job:${executionId}`;
}

function unitSpanId(event: { unitId: string; parentUnitId?: string; iterationIndex?: number }): string {
    return event.parentUnitId !== undefined || event.iterationIndex !== undefined
        ? `unit:${event.parentUnitId ?? "parent"}:${event.iterationIndex ?? "?"}:${event.unitId}`
        : `unit:${event.unitId}`;
}

function isAttemptSpanId(spanId: string, baseSpanId: string): boolean {
    return spanId === baseSpanId || spanId.startsWith(`${baseSpanId}#`);
}

function attemptNumber(spanId: string, baseSpanId: string): number {
    if (spanId === baseSpanId) return 1;
    if (!spanId.startsWith(`${baseSpanId}#`)) return 0;
    const parsed = Number(spanId.slice(baseSpanId.length + 1));
    return Number.isFinite(parsed) ? parsed : 0;
}

function allocateUnitAttemptSpanId(
    run: TraceRun,
    event: { unitId: string; parentUnitId?: string; iterationIndex?: number },
): string {
    const baseSpanId = unitSpanId(event);
    const attempts = run.spans.filter((span) =>
        span.kind === "unit" && isAttemptSpanId(span.id, baseSpanId),
    );
    if (attempts.length === 0) return baseSpanId;
    const running = [...attempts].reverse().find((span) => span.status === "running");
    if (running) return running.id;
    const nextAttempt = Math.max(1, ...attempts.map((span) => attemptNumber(span.id, baseSpanId))) + 1;
    return `${baseSpanId}#${nextAttempt}`;
}

function currentUnitAttemptSpanId(
    run: TraceRun,
    event: { unitId: string; parentUnitId?: string; iterationIndex?: number },
): string {
    const baseSpanId = unitSpanId(event);
    const attempts = run.spans.filter((span) =>
        span.kind === "unit" && isAttemptSpanId(span.id, baseSpanId),
    );
    const running = [...attempts].reverse().find((span) => span.status === "running");
    return running?.id ?? attempts.at(-1)?.id ?? baseSpanId;
}

function currentUnitAttemptSuffix(
    run: TraceRun,
    event: { unitId: string; parentUnitId?: string; iterationIndex?: number },
): string {
    const baseSpanId = unitSpanId(event);
    const currentSpanId = currentUnitAttemptSpanId(run, event);
    return currentSpanId === baseSpanId ? "" : currentSpanId.slice(baseSpanId.length);
}

function withAttemptSuffix(spanId: string, suffix: string): string {
    if (!suffix || spanId.endsWith(suffix)) return spanId;
    return `${spanId}${suffix}`;
}

function traceProgressSpanId(run: TraceRun, event: ExecutionProgressEvent, trace: TraceProgressEvent): string {
    const baseSpanId =
        trace.spanId ??
        `${event.unitId}:${trace.kind}:${trace.sidecarId ?? ""}:${trace.method ?? trace.name ?? ""}`;
    return withAttemptSuffix(baseSpanId, currentUnitAttemptSuffix(run, event));
}

function upsertSpan(spans: TraceSpan[], span: TraceSpan): TraceSpan[] {
    const index = spans.findIndex((item) => item.id === span.id);
    if (index < 0) return [...spans, span];
    const next = [...spans];
    next[index] = {
        ...next[index],
        ...span,
        attrs: {
            ...(next[index].attrs ?? {}),
            ...(span.attrs ?? {}),
        },
    };
    return next;
}

function appendEvent(run: TraceRun, event: Omit<TraceEventRecord, "id" | "traceId">): TraceRun {
    const nextEvent: TraceEventRecord = {
        id: `trace-event-${++eventSeq}`,
        traceId: run.traceId,
        ...event,
    };
    const events = [...run.events, nextEvent];
    let dropped = run.droppedCount;
    if (events.length > MAX_EVENTS_PER_RUN) {
        events.splice(0, events.length - MAX_EVENTS_PER_RUN);
        dropped += 1;
    }
    return {
        ...run,
        events,
        eventCount: run.eventCount + 1,
        droppedCount: dropped,
    };
}

function ensureRunFromJob(runs: TraceRun[], event: ExecutionJobChangedEvent): TraceRun[] {
    const traceId = event.traceId;
    if (!traceId) return runs;
    const existing = runs.find((run) => run.traceId === traceId);
    const completedAt = event.completedAt;
    const status = statusFromJob(event.status);
    const filePath = resolveEventFilePath(event);
    const timestamp = eventTimestamp(event);
    const base: TraceRun = existing ?? {
        traceId,
        executionId: event.executionId,
        sessionId: event.sessionId,
        filePath,
        target: event.target,
        unitId: event.unitId,
        unitIds: event.unitIds,
        action: event.action,
        source: "execution",
        status,
        startedAt: event.startedAt,
        completedAt,
        durationMs: durationBetween(event.startedAt, completedAt),
        baseRevision: event.baseRevision,
        currentRevision: event.currentRevision,
        eventCount: 0,
        droppedCount: 0,
        spans: [],
        events: [],
    };

    const run: TraceRun = {
        ...base,
        filePath: filePath ?? base.filePath,
        status,
        completedAt,
        durationMs: durationBetween(base.startedAt, completedAt),
        currentRevision: event.currentRevision ?? base.currentRevision,
        spans: upsertSpan(base.spans, {
            id: jobSpanId(event.executionId),
            traceId,
            kind: "job",
            name: event.target === "all" ? "Run all" : `Run ${event.unitId ?? "unit"}`,
            status,
            startedAt: event.startedAt,
            completedAt,
            durationMs: durationBetween(event.startedAt, completedAt),
            unitId: event.unitId,
            actionName: event.action,
            source: "execution",
            attrs: {
                phase: event.phase,
                target: event.target,
                stale: event.stale,
                message: event.message,
                executed: event.executed,
                failed: event.failed,
            },
            error: event.error,
        }),
    };

    const withEvent = appendEvent(run, {
        timestamp,
        type: "execution.jobChanged",
        spanId: jobSpanId(event.executionId),
        message: event.phase ?? event.status,
        attrs: { status: event.status, phase: event.phase },
    });

    const others = runs.filter((item) => item.traceId !== traceId);
    return [withEvent, ...others].slice(0, MAX_TRACE_RUNS);
}

function ensureRunFromTraceStarted(runs: TraceRun[], event: TraceStartedEvent): TraceRun[] {
    const traceId = event.traceId;
    const existing = runs.find((run) => run.traceId === traceId);
    const timestamp = eventTimestamp(event);
    const startedAt = event.startedAt ?? timestamp;
    const filePath = resolveEventFilePath(event);
    const base: TraceRun = existing ?? {
        traceId,
        executionId: event.executionId,
        sessionId: event.sessionId,
        filePath,
        target: event.target,
        unitId: event.unitId,
        unitIds: event.unitIds,
        action: event.action,
        source: event.source,
        extensionId: event.extensionId,
        method: event.method,
        status: "running",
        startedAt,
        eventCount: 0,
        droppedCount: 0,
        spans: [],
        events: [],
    };
    const run: TraceRun = {
        ...base,
        executionId: event.executionId,
        sessionId: event.sessionId,
        filePath: filePath ?? base.filePath,
        target: event.target,
        unitId: event.unitId,
        unitIds: event.unitIds,
        action: event.action,
        source: event.source ?? base.source,
        extensionId: event.extensionId ?? base.extensionId,
        method: event.method ?? base.method,
        status: "running",
        startedAt,
        completedAt: undefined,
        durationMs: undefined,
    };
    const withEvent = appendEvent(run, {
        timestamp,
        type: "trace.started",
        message: event.action ?? event.target,
        attrs: event as unknown as Record<string, unknown>,
    });
    const others = runs.filter((item) => item.traceId !== traceId);
    return [withEvent, ...others].slice(0, MAX_TRACE_RUNS);
}

function completeTraceRun(run: TraceRun, event: TraceCompletedEvent): TraceRun {
    const timestamp = eventTimestamp(event);
    const completedAt = event.completedAt ?? timestamp;
    const status = event.status;
    return appendEvent({
        ...run,
        status,
        completedAt,
        durationMs: event.durationMs ?? durationBetween(run.startedAt, completedAt),
    }, {
        timestamp,
        type: "trace.completed",
        message: event.message ?? event.error ?? status,
        attrs: event as unknown as Record<string, unknown>,
    });
}

function updateRun(
    runs: TraceRun[],
    traceId: string,
    updater: (run: TraceRun) => TraceRun,
): TraceRun[] {
    const index = runs.findIndex((run) => run.traceId === traceId);
    if (index < 0) return runs;
    const next = [...runs];
    next[index] = updater(next[index]);
    return next;
}

function progressTraceEvent(event: ExecutionProgressEvent): TraceProgressEvent | null {
    const trace = event.partialOutput?.trace;
    if (!trace || typeof trace !== "object") return null;
    const item = trace as TraceProgressEvent;
    return item.kind && item.state ? item : null;
}

function traceAttrString(attrs: Record<string, unknown> | undefined, key: string): string | undefined {
    const value = attrs?.[key];
    return typeof value === "string" ? value : undefined;
}

function handleTraceProgressSpan(run: TraceRun, event: ExecutionProgressEvent): TraceRun {
    const trace = progressTraceEvent(event);
    if (!trace) return run;
    const timestamp = trace.timestamp ?? eventTimestamp(event);
    const spanId = traceProgressSpanId(run, event, trace);
    const attemptSuffix = currentUnitAttemptSuffix(run, event);
    const parentId = trace.parentSpanId
        ? withAttemptSuffix(trace.parentSpanId, attemptSuffix)
        : inferTraceParentSpanId(run, event, trace);
    const status =
        trace.status ??
        (trace.state === "failed"
            ? "failed"
            : trace.state === "completed"
                ? "completed"
                : "running");
    const completedAt = trace.state === "started" || trace.state === "event"
        ? undefined
        : timestamp;
    const existing = run.spans.find((span) => span.id === spanId);
    const startedAt = existing?.startedAt ??
        (trace.durationMs != null && completedAt
            ? new Date(new Date(completedAt).getTime() - trace.durationMs).toISOString()
            : timestamp);

    const withSpan: TraceRun = {
        ...run,
        spans: upsertSpan(run.spans, {
            id: spanId,
            traceId: run.traceId,
            parentId,
            kind: trace.kind,
            name: trace.name ?? event.detail ?? trace.kind,
            status,
            startedAt,
            completedAt,
            durationMs: trace.durationMs ?? durationBetween(startedAt, completedAt),
            unitId: event.unitId,
            unitType: event.unitType,
            pluginName: traceAttrString(trace.attrs, "pluginName"),
            actionName: traceAttrString(trace.attrs, "actionName"),
            sidecarId: trace.sidecarId,
            method: trace.method,
            attrs: trace.attrs,
            error: trace.error,
        }),
    };

    return appendEvent(withSpan, {
        timestamp,
        type: `trace.${trace.kind}.${trace.state}`,
        spanId,
        unitId: event.unitId,
        message: trace.message ?? event.detail,
        attrs: trace.attrs,
    });
}

function handleStandaloneTraceProgress(run: TraceRun, event: TraceProgressNotification): TraceRun {
    const trace = event.trace;
    if (!trace || !trace.kind || !trace.state) return run;
    const timestamp = trace.timestamp ?? eventTimestamp(event);
    const spanId =
        trace.spanId ??
        `${trace.kind}:${event.extensionId ?? event.method ?? trace.name ?? "event"}:${run.eventCount + 1}`;
    const status =
        trace.status ??
        (trace.state === "failed"
            ? "failed"
            : trace.state === "completed"
                ? "completed"
                : "running");
    const completedAt = trace.state === "started" || trace.state === "event"
        ? undefined
        : timestamp;
    const existing = run.spans.find((span) => span.id === spanId);
    const startedAt = existing?.startedAt ??
        (trace.durationMs != null && completedAt
            ? new Date(new Date(completedAt).getTime() - trace.durationMs).toISOString()
            : timestamp);
    const attrs = {
        ...(trace.attrs ?? {}),
        ...(event.source ? { source: event.source } : {}),
        ...(event.extensionId ? { extensionId: event.extensionId } : {}),
        ...(event.method ? { method: event.method } : {}),
    };

    const withSpan: TraceRun = {
        ...run,
        filePath: resolveEventFilePath(event) ?? run.filePath,
        source: event.source ?? run.source,
        extensionId: event.extensionId ?? run.extensionId,
        method: event.method ?? run.method,
        spans: upsertSpan(run.spans, {
            id: spanId,
            traceId: run.traceId,
            parentId: trace.parentSpanId,
            kind: trace.kind,
            name: trace.name ?? event.detail ?? trace.kind,
            status,
            startedAt,
            completedAt,
            durationMs: trace.durationMs ?? durationBetween(startedAt, completedAt),
            unitId: event.unitId,
            unitType: event.unitType,
            source: event.source,
            extensionId: event.extensionId,
            method: trace.method ?? event.method,
            sidecarId: trace.sidecarId,
            attrs,
            error: trace.error,
        }),
    };

    return appendEvent(withSpan, {
        timestamp,
        type: `trace.${trace.kind}.${trace.state}`,
        spanId,
        unitId: event.unitId,
        message: trace.message ?? event.detail ?? trace.name,
        attrs,
    });
}

function inferTraceParentSpanId(
    run: TraceRun,
    event: ExecutionProgressEvent,
    trace: TraceProgressEvent,
): string | undefined {
    const attemptSuffix = currentUnitAttemptSuffix(run, event);
    if (trace.kind === "plugin-api") {
        return undefined;
    }
    if (
        trace.kind === "sidecar-call" ||
        trace.kind === "sidecar-ensure" ||
        trace.kind === "phase"
    ) {
        const pluginOperation = [...run.spans]
            .reverse()
            .find((span) =>
                (span.kind === "plugin-api" || span.kind === "plugin-action") &&
                span.unitId === event.unitId &&
                (!attemptSuffix || span.id.endsWith(attemptSuffix)),
            );
        if (pluginOperation) return pluginOperation.id;
    }
    return currentUnitAttemptSpanId(run, event);
}

function closeSpan(
    run: TraceRun,
    spanId: string,
    status: TraceSpanStatus,
    completedAt: string,
    patch?: Partial<TraceSpan>,
): TraceRun {
    const existing = run.spans.find((span) => span.id === spanId);
    if (!existing) return run;
    return {
        ...run,
        spans: upsertSpan(run.spans, {
            ...existing,
            ...patch,
            status,
            completedAt,
            durationMs: patch?.durationMs ?? durationBetween(existing.startedAt, completedAt),
        }),
    };
}

export const useTraceStore = create<TraceState>((set, get) => ({
    runs: [],
    selectedTraceId: null,
    recording: false,

    setRecording: (recording) => set({ recording }),
    selectTrace: (traceId) => set({ selectedTraceId: traceId }),
    importTrace: (run) => set((state) => {
        const existingIndex = state.runs.findIndex((item) => item.traceId === run.traceId);
        const nextRun = {
            ...run,
            eventCount: run.eventCount ?? run.events.length,
            droppedCount: run.droppedCount ?? 0,
            spans: run.spans ?? [],
            events: run.events ?? [],
        };
        const runs = existingIndex >= 0
            ? state.runs.map((item, index) => index === existingIndex ? nextRun : item)
            : [nextRun, ...state.runs].slice(0, MAX_TRACE_RUNS);
        return {
            runs,
            selectedTraceId: run.traceId,
        };
    }),
    deleteTrace: (traceId) => set((state) => {
        const runs = state.runs.filter((run) => run.traceId !== traceId);
        return {
            runs,
            selectedTraceId: state.selectedTraceId === traceId
                ? runs[0]?.traceId ?? null
                : state.selectedTraceId,
        };
    }),
    clear: () => set({ runs: [], selectedTraceId: null }),

    exportTrace: (traceId) => {
        const run = get().runs.find((item) => item.traceId === traceId);
        return run ? JSON.stringify(run, null, 2) : null;
    },

    ingestNotification: (method, params) => {
        if (!get().recording) return;

        if (method === "event/execution.jobChanged") {
            const event = params as ExecutionJobChangedEvent;
            if (!event.traceId) return;
            set((state) => {
                const runs = ensureRunFromJob(state.runs, event);
                return {
                    runs,
                    selectedTraceId: state.selectedTraceId ?? event.traceId,
                };
            });
            return;
        }

        if (method === "event/trace.started") {
            const event = params as TraceStartedEvent;
            if (!event.traceId) return;
            set((state) => ({
                runs: ensureRunFromTraceStarted(state.runs, event),
                selectedTraceId: state.selectedTraceId ?? event.traceId,
            }));
            return;
        }

        if (method === "event/trace.completed") {
            const event = params as TraceCompletedEvent;
            if (!event.traceId) return;
            set((state) => ({
                runs: updateRun(state.runs, event.traceId, (run) =>
                    completeTraceRun(run, event),
                ),
            }));
            return;
        }

        if (method === "event/trace.progress") {
            const event = params as TraceProgressNotification;
            if (!event.traceId) return;
            set((state) => ({
                runs: updateRun(state.runs, event.traceId, (run) =>
                    handleStandaloneTraceProgress(run, event),
                ),
            }));
            return;
        }

        const traceId = getTraceId(params);
        if (!traceId) {
            if (
                method === "event/sidecar.spawned" ||
                method === "event/sidecar.stopped" ||
                method === "event/sidecar.restarted"
            ) {
                const active = get().runs.find((run) => run.status === "running");
                if (!active) return;
                set((state) => ({
                    runs: updateRun(state.runs, active.traceId, (run) =>
                        appendEvent(run, {
                            timestamp: nowIso(),
                            type: method.replace("event/", ""),
                            message:
                                method === "event/sidecar.spawned"
                                    ? (params as SidecarSpawnedEvent).sidecarId
                                    : method === "event/sidecar.stopped"
                                        ? (params as SidecarStoppedEvent).sidecarId
                                        : (params as SidecarRestartedEvent).sidecarId,
                            attrs: params as Record<string, unknown>,
                        }),
                    ),
                }));
            }
            return;
        }

        set((state) => ({
            runs: updateRun(state.runs, traceId, (run) => {
                const timestamp = eventTimestamp(params);
                const runWithContext = {
                    ...run,
                    filePath: resolveEventFilePath(params) ?? run.filePath,
                };
                switch (method) {
                    case "event/execution.started": {
                        const event = params as ExecutionStartedEvent;
                        const spanId = allocateUnitAttemptSpanId(runWithContext, event);
                        return appendEvent({
                            ...runWithContext,
                            spans: upsertSpan(runWithContext.spans, {
                                id: spanId,
                                traceId,
                                parentId: event.parentUnitId
                                    ? unitSpanId({
                                        unitId: event.parentUnitId,
                                    })
                                    : jobSpanId(event.executionId ?? runWithContext.executionId),
                                kind: "unit",
                                name: `${event.unitId} ${event.pluginName ?? ""}.${event.actionName ?? ""}`.trim(),
                                status: "running",
                                startedAt: timestamp,
                                unitId: event.unitId,
                                unitType: event.unitType,
                                pluginName: event.pluginName,
                                actionName: event.actionName,
                                attrs: { progress: event.progress },
                            }),
                        }, {
                            timestamp,
                            type: "execution.started",
                            spanId,
                            unitId: event.unitId,
                            message: event.actionName,
                            attrs: event as unknown as Record<string, unknown>,
                        });
                    }
                    case "event/execution.progress": {
                        const event = params as ExecutionProgressEvent;
                        const withTraceSpan = handleTraceProgressSpan(runWithContext, event);
                        if (withTraceSpan !== runWithContext) return withTraceSpan;
                        const spanId = currentUnitAttemptSpanId(runWithContext, event);
                        return appendEvent(runWithContext, {
                            timestamp,
                            type: "execution.progress",
                            spanId,
                            unitId: event.unitId,
                            message: event.detail,
                            // partialOutput can contain the complete live query result.
                            // The execution store already owns the latest value; retaining
                            // it in every trace event multiplies large outputs by thousands.
                            attrs: {
                                phase: event.phase,
                                percent: event.percent,
                            },
                        });
                    }
                    case "event/execution.completed": {
                        const event = params as ExecutionCompletedEvent;
                        const spanId = currentUnitAttemptSpanId(runWithContext, event);
                        return appendEvent(
                            closeSpan(runWithContext, spanId, "completed", timestamp, {
                                durationMs: event.meta.duration,
                            }),
                            {
                                timestamp,
                                type: "execution.completed",
                                spanId,
                                unitId: event.unitId,
                                message: event.meta.actionName,
                                attrs: {
                                    duration: event.meta.duration,
                                    actionName: event.meta.actionName,
                                },
                            },
                        );
                    }
                    case "event/execution.failed": {
                        const event = params as ExecutionFailedEvent;
                        const spanId = currentUnitAttemptSpanId(runWithContext, event);
                        const status = event.error === "Execution cancelled by user"
                            ? "cancelled"
                            : "failed";
                        return appendEvent(
                            closeSpan(runWithContext, spanId, status, timestamp, {
                                error: event.error,
                            }),
                            {
                                timestamp,
                                type: "execution.failed",
                                spanId,
                                unitId: event.unitId,
                                message: event.error,
                                attrs: event as unknown as Record<string, unknown>,
                            },
                        );
                    }
                    case "event/execution.cascade": {
                        const event = params as ExecutionCascadeEvent;
                        return appendEvent(runWithContext, {
                            timestamp,
                            type: "execution.cascade",
                            unitId: event.sourceUnitId,
                            message: `${event.sourceUnitId} -> ${event.targetUnitIds.join(", ")}`,
                            attrs: event as unknown as Record<string, unknown>,
                        });
                    }
                    case "event/execution.iterationStarted": {
                        const event = params as ExecutionIterationStartedEvent;
                        return appendEvent(runWithContext, {
                            timestamp,
                            type: "execution.iterationStarted",
                            spanId: unitSpanId(event),
                            unitId: event.unitId,
                            message: `${event.index + 1}/${event.total}`,
                            attrs: event as unknown as Record<string, unknown>,
                        });
                    }
                    case "event/execution.iterationCompleted": {
                        const event = params as ExecutionIterationCompletedEvent;
                        return appendEvent(runWithContext, {
                            timestamp,
                            type: "execution.iterationCompleted",
                            spanId: unitSpanId(event),
                            unitId: event.unitId,
                            message: `${event.index + 1}`,
                            attrs: {
                                index: event.index,
                                success: event.iteration.success,
                                error: event.iteration.error,
                            },
                        });
                    }
                    default:
                        return runWithContext;
                }
            }),
        }));
    },
}));

let registeredBus: ReturnType<typeof getNotificationBus> | null = null;
let unsubscribeTraceHandlers: (() => void) | null = null;

export function ensureTraceNotificationHandlers(): void {
    const bus = getNotificationBus();
    if (registeredBus === bus && unsubscribeTraceHandlers) return;
    unsubscribeTraceHandlers?.();
    registeredBus = bus;

    const methods = [
        "event/trace.started",
        "event/trace.progress",
        "event/trace.completed",
        "event/execution.jobChanged",
        "event/execution.started",
        "event/execution.progress",
        "event/execution.completed",
        "event/execution.failed",
        "event/execution.cascade",
        "event/execution.iterationStarted",
        "event/execution.iterationCompleted",
        "event/sidecar.spawned",
        "event/sidecar.stopped",
        "event/sidecar.restarted",
    ] as const;

    const unsubs = methods.map((method) =>
        bus.subscribe(method, (params) => {
            useTraceStore.getState().ingestNotification(method, params);
        }),
    );
    unsubscribeTraceHandlers = () => {
        for (const unsub of unsubs) unsub();
    };
}
